import moment from 'moment';
import {DateInput, DateRange} from './DatePicker';

type Mode = 'single' | 'range' | 'datetime' | 'time';
interface ParseOptions {mode?: Mode; format?: string; base?: DateInput; min?: DateInput; max?: DateInput; required?: boolean;}
interface ParseResult {value?: Date | DateRange | null; error?: string;}

export function formatFieldValue(value: DateInput | DateRange | null, mode: Mode, format: string): string {
  const part = date => date == null ? '' : moment(date).locale('en').format(format);
  if (mode === 'range') {const range = value as DateRange; return range && (range.start || range.end) ? `${part(range.start)} — ${part(range.end)}` : '';}
  return part(value);
}

// Strict parsing prevents JS Date from rolling 31 February into March.
// Numeric dates use the school's existing UK day/month convention.
export function parseFieldValue(text: string, {mode = 'single', format = 'DD MMM YYYY', base, min, max, required = false}: ParseOptions = {}): ParseResult {
  const input = text.trim();
  const fail = error => ({error});
  if (!input) return required ? fail('Enter a value.') : {value: mode === 'range' ? {start: null, end: null} : null};
  const numeric = ['DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY', 'D-M-YYYY', 'YYYY-MM-DD'];
  const dates = [...numeric, 'DD MMM YYYY', 'D MMM YYYY'];
  const formats = mode === 'datetime'
    ? [format, ...dates.reduce((all, date) => all.concat([`${date} HH:mm`, `${date} H:mm`, `${date} hh:mm A`, `${date} h:mm A`]), [] as string[])]
    : mode === 'time' ? [format, 'HH:mm', 'H:mm', 'h:mm A', 'h A', 'Hmm', 'H'] : [format, ...dates];
  const parse = value => {
    const parsed = moment(value.trim(), formats, 'en', true);
    // Moment accepts 24:00 as tomorrow's midnight; a field must not silently change the date.
    if (!parsed.isValid() || parsed.parsingFlags().parsedDateParts[3] === 24) return null;
    if (mode === 'time') {
      const day = base != null ? moment(base).toDate() : new Date();
      day.setHours(parsed.hours(), parsed.minutes(), 0, 0);
      return day;
    }
    return parsed.toDate();
  };
  const rank = value => {
    const date = moment(value);
    return mode === 'time' ? date.hours() * 60 + date.minutes() : mode === 'datetime' ? +date : +date.startOf('day');
  };
  const bounds = value => (min != null && rank(value) < rank(min)) || (max != null && rank(value) > rank(max));
  if (mode === 'range') {
    const parts = input.split(/\s+(?:—|–|-|to)\s+/i);
    if (parts.length !== 2) return fail('Enter two dates, for example 01/10/2026 — 31/10/2026.');
    const start = parse(parts[0]), end = parse(parts[1]);
    if (!start || !end) return fail('Enter valid dates, for example 01/10/2026 — 31/10/2026.');
    if (start > end) return fail('The end date must be on or after the start date.');
    if (bounds(start) || bounds(end)) return fail('Choose dates within the allowed range.');
    return {value: {start, end}};
  }
  const value = parse(input);
  if (!value) return fail(mode === 'time' ? 'Enter a valid time, for example 09:30.' : mode === 'datetime' ? 'Enter a valid date and time, for example 26/10/2026 09:30.' : 'Enter a valid date, for example 26/10/2026.');
  if (bounds(value)) return fail('Choose a value within the allowed range.');
  return {value};
}
